import { useTimeoutFn } from '@/composables/useTimeoutFn';
import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { ref, Ref } from 'vue';

const MAX_TOASTS = 5;
const TOAST_DURATION = 5 * 1000;

export enum TOAST_TYPE {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warn',
  INFO = 'info',
}

export interface ToastButtonAction {
  title: string;
  icon?: string;
  handler?: () => void;
}

export interface ToastOptions {
  title?: string;
  icon?: string;
  onClose?: (toast: ToastMessage) => void;
  actions?: ToastButtonAction[];
}

export interface ToastMessage {
  id?: string;
  message: string;
  type: TOAST_TYPE;
  timer?: {
    start: () => void;
    resume: () => void;
    pause: () => void;
  };
  options?: ToastOptions;
}

export const useToastStore = defineStore('toastStore', () => {
  const queue: Ref<ToastMessage[]> = ref([]);
  const toDisplay: Ref<ToastMessage[]> = ref([]);

  const addToast = (toast: ToastMessage) => {
    toast.id = uuidv4();

    if (toDisplay.value.length >= MAX_TOASTS) {
      queue.value.push(toast);
      return;
    }

    return showSingleToast(toast);
  };

  const showSingleToast = (msg: ToastMessage) => {
    toDisplay.value.unshift(msg);

    msg.timer = useTimeoutFn(() => removeToast(msg), TOAST_DURATION, true);
    return msg;
  };

  const removeToast = (toast: ToastMessage) => {
    toDisplay.value = toDisplay.value.filter(t => t.id !== toast.id);

    if (toast.options?.onClose) {
      toast.options?.onClose(toast);
    }

    checkQueue();
  };

  const clearToasts = () => {
    queue.value = [];
    toDisplay.value = [];
  };

  const checkQueue = () => {
    if (!queue.value.length) {
      return;
    }

    const toRemove: ToastMessage[] = [];
    queue.value.forEach(msg => {
      if (toDisplay.value.length >= MAX_TOASTS) {
        return;
      }

      toRemove.push(msg);
      showSingleToast(msg);
    });

    queue.value = queue.value.filter(t => toRemove.indexOf(t));
  };

  return { queue, toDisplay, addToast, showSingleToast, removeToast, clearToasts };
});
